'use client'

// The profile this wallet already has on Ordinals Wallet.
//
// Someone who set a name and a picture here should not have to set them again
// to post: a new Twetch profile opens with what OW already knows, and they
// change it or not. OW stores its avatar as the inscription's id, which is
// exactly what a Twetch avatar ref carries.

import { STORAGE_PROFILE } from '@/utils/constants'
import type { DeviceKey } from './twetch-crypto'

const INSCRIPTION_ID = /^[0-9a-fA-F]{64}i\d+$/

export interface OwProfile {
	name: string | null
	/// The avatar as a Twetch media ref (`ord://<inscription>`), when the OW
	/// profile has one.
	iconRef: string | null
}

export default function readOwProfile(): OwProfile {
	if (typeof window === 'undefined') {
		return { name: null, iconRef: null }
	}
	try {
		const raw = window.localStorage.getItem(STORAGE_PROFILE)
		const parsed = raw ? (JSON.parse(raw) as { icon?: unknown; sns?: unknown }) : {}
		const icon = typeof parsed.icon === 'string' ? parsed.icon.trim() : ''
		const sns = typeof parsed.sns === 'string' ? parsed.sns.trim() : ''
		return {
			name: sns || null,
			iconRef: INSCRIPTION_ID.test(icon) ? `ord://${icon}` : null
		}
	} catch {
		// No profile stored, or storage is unavailable: nothing to prefill.
		return { name: null, iconRef: null }
	}
}

/// Carry an Ordinals Wallet profile onto a Twetch account that has just been
/// created — the first-post path mints an account with no name and no picture,
/// and someone who already set both here should not arrive blank.
///
/// Best effort by design: a refused name (taken, reserved) or a failed write
/// must never cost someone their post. They can still edit the profile.
export async function migrateOwProfile(auth: {
	uid: number
	deviceKey: DeviceKey
}): Promise<void> {
	const { name, iconRef } = readOwProfile()
	if (!name && !iconRef) return
	try {
		const { twetchSignedPatch } = await import('./twetch-transport')
		await twetchSignedPatch(
			`/v1/users/${auth.uid}`,
			{ ...(name ? { name } : {}), ...(iconRef ? { icon: iconRef } : {}) },
			auth
		)
	} catch {
		// Nothing was carried over; the profile editor still offers the same
		// values the next time it opens.
	}
}
